import {
  BlurFilter,
  Container,
  Graphics,
  PerspectiveMesh,
  Rectangle,
  Texture,
  type DestroyOptions,
} from 'pixi.js';
import { padOfTexture, type FaceSize } from './metrics';
import { projectCorners, type CardCorners } from './projection';
import { resolvePose, type CardPose, type PoseInput } from './pose';
import { t } from '../../i18n';

/**
 * **通用卡牌节点** —— 所有牌共用的一份渲染。
 *
 * 它做三件事，一件也不多做：
 *
 * 1. 拿一张**牌面纹理**贴到网格上
 * 2. 按 `CardPose` 把网格**假透视**成四边形的样子
 * 3. 把自己摆到该在的位置
 *
 * 它**不认识任何具体的牌** —— 牌面从哪来是 `CardFaceCache` 的事，
 * 长什么样是 `CardArt` 的事。所以加一张新牌不需要动这个文件。
 *
 * ## ⚠️ 两块四边形，别搞混
 *
 * 牌面纹理是**含出血的**（`metrics.FACE_PAD_RATIO`），所以这个节点上有两组四角：
 *
 * | | 是什么 | 谁用 |
 * | --- | --- | --- |
 * | `corners` | **牌本身**，不含出血 | 挖洞遮罩、命中测试、阴影 |
 * | `bleedCorners` | 含出血的那块纹理，比牌大一圈 | 网格（贴图要铺满整块纹理） |
 *
 * 混用它们两个的后果很隐蔽：用 `bleedCorners` 去挖洞，桌面上的洞会比牌大一圈，
 * 于是牌周围浮着一圈假的暗边；用 `corners` 去铺网格，伸到牌外的图形全部被切掉。
 *
 * ## 为什么缩放走 `scale` 而不是算进角点
 *
 * 因为算进角点的话，将来挂上去的阴影不会跟着放大。
 * 而且数学上等价：模型和相机距离同比例放大，投影结果就是整体等比放大
 * （`test/render/projection.test.ts` 里钉住了这条）。
 */

/**
 * 网格密度。
 *
 * `PerspectiveMesh` 的透视是在**顶点**层面算的，顶点越密越平滑。
 * 20×20 是 Pixi 自己的建议默认，肉眼已经看不出折线；2×2 会明显拉伸。
 *
 * 不去暴露成参数：这是**画质与性能的取舍**，不是每张牌各自的艺术选择，
 * 全项目一个值就够了。真要调，改这里。
 */
const VERTEX_DENSITY = { x: 20, y: 20 } as const;

/**
 * **选中光晕往外扩多少**（相对牌心到四角的距离）。
 *
 * 用户：「外发光程度还是有些过大」。所以它很小 —— 只是给牌沿描一圈光，
 * 不是把牌放大一圈。模糊半径也跟着小（见 `setGlow`）。
 */
const GLOW_EXPAND = 0.07;

/** 选中光晕的**出现 / 消失时长**（毫秒）—— 用户要求 350ms 的透明度过渡 */
export const GLOW_FADE_MS = 350;
/** 光晕的最大不透明度 */
const GLOW_ALPHA = 0.8;

export interface CardViewOptions {
  /** 牌面纹理。通常是 `CardFaceCache.get()` 的返回值 */
  readonly texture: Texture;
  /**
   * **牌本身**的逻辑尺寸（不含出血）。省略 = 纹理尺寸，也就是没有出血。
   *
   * ⚠️ 必须和 `CardFaceCache.get()` 用的是同一个 `size`，
   * 否则出血宽度会被算错，牌要么缩水要么溢出。
   */
  readonly size?: FaceSize;
  /** 初始姿态。省略 = 平放正对镜头 */
  readonly pose?: PoseInput;
  /** 网格密度覆盖。**只在确有理由时用** */
  readonly verticesX?: number;
  readonly verticesY?: number;
  /**
   * **裁到牌框内**（去掉出血）。表格 / 缩略图 / 牌堆这类"牌彼此紧挨"的场合用。
   *
   * 实现是给纹理换一个**裁掉出血的子帧**、网格铺 `corners`（而不是 `bleedCorners`）——
   * 不做遮罩、不多一个显示对象。手牌与居中牌保持 `false`（它们要出血）。
   */
  readonly clip?: boolean;
}

export class CardView extends Container {
  /** 底下的网格。想改 tint / blendMode 直接对它动手 */
  readonly face: PerspectiveMesh;

  /** **牌本身**的逻辑尺寸（由调用方给出，不是纹理尺寸 —— 纹理含出血） */
  readonly size: FaceSize;

  /** 出血宽度（逻辑像素）。0 = 这张纹理没有出血 */
  readonly pad: number;

  #pose: CardPose;
  #corners: CardCorners;
  #bleedCorners: CardCorners;
  readonly #clip: boolean;
  /** 选中态的发光块（没选中时 null） */
  #glow: Graphics | null = null;
  #glowFilter: BlurFilter | null = null;
  /** 光晕颜色。换色时只重画，不重建节点 */
  #glowColor = 0xffffff;
  /** 光晕的目标状态（1 = 该亮，0 = 该灭），透明度按 350ms 补间过去 */
  #glowTarget = 0;
  /** 现在的不透明度进度 0..1 */
  #glowAlpha = 0;

  constructor(options: CardViewOptions) {
    super();

    const { texture } = options;
    const size = options.size ?? { width: texture.width, height: texture.height };
    this.size = size;
    this.pad = padOfTexture(texture.width, size);
    if (this.pad < 0) {
      throw new Error(
        t('render.cardView.textureTooSmall', {
          texW: texture.width,
          texH: texture.height,
          sizeW: size.width,
          sizeH: size.height,
        }),
      );
    }

    this.#pose = resolvePose(options.pose);
    this.#clip = options.clip ?? false;

    // ⚠️ 四个角是相对卡牌**中心**的，所以网格的原点就是牌的中心。
    // 这样旋转天然绕中心，`position` 也就直接是"牌在哪"。
    this.#corners = projectCorners(this.#pose, size);
    this.#bleedCorners = projectCorners(this.#pose, size, this.pad);

    // clip：纹理换成"裁掉出血的子帧"，网格铺牌框（corners）；否则铺含出血的 bleedCorners
    this.face = new PerspectiveMesh({
      texture: this.#clip ? this.#crop(texture) : texture,
      verticesX: options.verticesX ?? VERTEX_DENSITY.x,
      verticesY: options.verticesY ?? VERTEX_DENSITY.y,
      ...(this.#clip ? this.#corners : this.#bleedCorners),
    });
    this.addChild(this.face);

    this.label = 'card-view';
    this.#apply();
  }

  /** 当前姿态（补全默认值之后的完整对象，只读） */
  get pose(): Readonly<CardPose> {
    return this.#pose;
  }

  /** **牌本身**的四角。挖洞遮罩、命中测试读它 */
  get corners(): Readonly<CardCorners> {
    return this.#corners;
  }

  /** 含出血的四角。网格铺的是它，一般不直接读 */
  get bleedCorners(): Readonly<CardCorners> {
    return this.#bleedCorners;
  }

  /** 换姿态。位置、缩放、两组四角一次改完 */
  setPose(input: PoseInput): void {
    this.#pose = resolvePose({ ...this.#pose, ...input });
    this.#corners = projectCorners(this.#pose, this.size);
    this.#bleedCorners = projectCorners(this.#pose, this.size, this.pad);
    const corners = this.#clip ? this.#corners : this.#bleedCorners;
    this.face.setCorners(
      corners.x0,
      corners.y0,
      corners.x1,
      corners.y1,
      corners.x2,
      corners.y2,
      corners.x3,
      corners.y3,
    );
    this.#apply();
  }

  /**
   * 换牌面。
   *
   * ⚠️ `PerspectiveMesh` 的源矩形跟着纹理尺寸走，所以换了**尺寸不同**的纹理
   * 会让已有的角点全部失真。牌面缓存按尺寸分桶，所以正常情况下
   * 同一个 `CardView` 只会换到同尺寸的纹理 —— 尺寸变了就重建它。
   */
  setTexture(texture: Texture): void {
    const expect = this.size.width + this.pad * 2;
    if (texture.width !== expect || texture.height !== this.size.height + this.pad * 2) {
      throw new Error(
        t('render.cardView.textureResized', {
          expectW: expect,
          expectH: this.size.height + this.pad * 2,
          texW: texture.width,
          texH: texture.height,
        }),
      );
    }
    this.face.texture = this.#clip ? this.#crop(texture) : texture;
  }

  /**
   * **选中态的外发光**：在牌底下垫一块**沿牌的四角往外扩一圈、又高斯模糊**
   * 的实心多边形。
   *
   * ⚠️ 多边形用 `corners`（牌的**投影四角**，含 pitch / roll），不是轴对齐
   *    圆角块 —— 圆角块不会跟着牌的俯仰和旋转走，牌一歪光晕就露馅（用户）。
   *    所以 `setPose` 改了四角之后要重画（见 `#apply` / `#paintGlow`）。
   *
   * ⚠️ 不用牌面纹理的拷贝：牌面本身的暗像素乘上颜色后还是暗的，看起来是
   *    "重影"而不是"发光"。实心块 + 加色混合才有真正的光晕。
   *
   * 幂等：同一个值重复调不重画；关掉时拆干净（连滤镜一起 destroy）。
   * 只有手牌那套会调它（HandView 按选中集合每帧同步）—— 其他用 CardView
   * 的地方不调，就一点开销都没有。
   */
  setGlow(on: boolean, color = 0xffffff): void {
    this.#glowTarget = on ? 1 : 0;
    if (!on) {
      // ⚠️ 不立刻拆：`stepGlow` 会把它淡到 0 再销毁（350ms 淡出）
      return;
    }
    if (this.#glow === null) {
      const halo = new Graphics();
      halo.blendMode = 'add';
      halo.alpha = 0;
      halo.filters = [new BlurFilter({ strength: Math.max(5, this.size.width * 0.09), quality: 4 })];
      this.addChildAt(halo, 0);
      this.#glow = halo;
      this.#glowFilter = halo.filters[0] as BlurFilter;
      this.#glowColor = color;
      this.#paintGlow();
      return;
    }
    if (this.#glowColor !== color) {
      this.#glowColor = color;
      this.#paintGlow();
    }
  }

  /**
   * **推进光晕的出现 / 消失**（350ms 透明度过渡）。宿主每帧调一次。
   *
   * 淡到 0 才真正销毁节点（连滤镜），所以"选中 → 取消"是渐隐而不是瞬灭。
   */
  stepGlow(deltaMS: number): void {
    const halo = this.#glow;
    if (halo === null && this.#glowTarget === 0) return;
    const step = GLOW_FADE_MS <= 0 ? 1 : Math.max(0, deltaMS) / GLOW_FADE_MS;
    if (this.#glowTarget > this.#glowAlpha) {
      this.#glowAlpha = Math.min(this.#glowTarget, this.#glowAlpha + step);
    } else if (this.#glowTarget < this.#glowAlpha) {
      this.#glowAlpha = Math.max(this.#glowTarget, this.#glowAlpha - step);
    }
    if (this.#glow === null) return;
    this.#glow.alpha = this.#glowAlpha * GLOW_ALPHA;
    if (this.#glowTarget === 0 && this.#glowAlpha <= 0.001) this.#clearGlow();
  }

  /** 按当前四角重画光晕多边形（pitch / roll 都跟着走） */
  #paintGlow(): void {
    const halo = this.#glow;
    if (halo === null) return;
    const c = this.#corners;
    const cx = (c.x0 + c.x1 + c.x2 + c.x3) / 4;
    const cy = (c.y0 + c.y1 + c.y2 + c.y3) / 4;
    const k = 1 + GLOW_EXPAND;
    const px = (x: number): number => cx + (x - cx) * k;
    const py = (y: number): number => cy + (y - cy) * k;
    halo
      .clear()
      .poly([px(c.x0), py(c.y0), px(c.x1), py(c.y1), px(c.x2), py(c.y2), px(c.x3), py(c.y3)])
      .fill({ color: this.#glowColor, alpha: 1 });
  }

  #clearGlow(): void {
    if (this.#glow !== null) {
      this.#glow.filters = [];
      this.#glow.destroy();
      this.#glow = null;
    }
    this.#glowFilter?.destroy();
    this.#glowFilter = null;
  }

  override destroy(options?: DestroyOptions): void {
    this.#clearGlow();
    super.destroy(options);
  }

  /** 裁掉出血的子帧（只保留牌框那一段）。见 `clip` */
  #crop(texture: Texture): Texture {
    const pad = this.pad;
    return new Texture({
      source: texture.source,
      frame: new Rectangle(texture.frame.x + pad, texture.frame.y + pad, this.size.width, this.size.height),
    });
  }

  #apply(): void {
    this.position.set(this.#pose.x, this.#pose.y);
    this.scale.set(this.#pose.scale);
    // 姿态变了 → 四角变了 → 光晕多边形跟着重画（pitch / roll 都跟得上）
    this.#paintGlow();
  }
}
